import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  ExitToApp as LogoutIcon,
  Security as SecurityIcon,
  Computer as DeviceIcon,
  AccessTime as TimeIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

interface Session {
  id: string;
  device_info: string;
  ip_address: string;
  last_activity: string;
  is_current: boolean;
  created_at: string;
}

interface SessionManagerProps {
  open: boolean;
  onClose: () => void;
}

export const SessionManager: React.FC<SessionManagerProps> = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [sessionToTerminate, setSessionToTerminate] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Load active sessions
  const loadSessions = async () => {
    if (!open) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Mock sessions for now - replace with actual API call
      const mockSessions: Session[] = [
        {
          id: '1',
          device_info: 'Windows 10 - Chrome 118.0',
          ip_address: '192.168.1.100',
          last_activity: new Date().toISOString(),
          is_current: true,
          created_at: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
        },
        {
          id: '2',
          device_info: 'iPhone 15 - Safari',
          ip_address: '192.168.1.105',
          last_activity: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
          is_current: false,
          created_at: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
        }
      ];
      
      setSessions(mockSessions);
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  // Terminate a specific session
  const terminateSession = async (sessionId: string) => {
    try {
      setLoading(true);
      
      // Mock API call - replace with actual implementation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      setSnackbarMessage('Session terminated successfully');
      setSnackbarOpen(true);
      setSessionToTerminate(null);
    } catch (err: any) {
      setError(err.message || 'Failed to terminate session');
    } finally {
      setLoading(false);
    }
  };

  // Logout from all sessions
  const logoutAllSessions = async () => {
    try {
      setLoading(true);
      await logout();
      setLogoutConfirmOpen(false);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to logout from all sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, [open]);

  const handleTerminateClick = (sessionId: string) => {
    setSessionToTerminate(sessionId);
  };

  const handleTerminateConfirm = () => {
    if (sessionToTerminate) {
      terminateSession(sessionToTerminate);
    }
  };

  const getSessionStatusColor = (session: Session) => {
    if (session.is_current) return 'primary';
    const lastActivity = new Date(session.last_activity);
    const hoursAgo = (Date.now() - lastActivity.getTime()) / (1000 * 60 * 60);
    return hoursAgo > 24 ? 'error' : 'success';
  };

  const getSessionStatusText = (session: Session) => {
    if (session.is_current) return 'Current Session';
    const lastActivity = new Date(session.last_activity);
    return `Active ${formatDistanceToNow(lastActivity)} ago`;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon />
            <Typography variant="h6">Session Management</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Manage your active sessions across all devices. You can terminate sessions 
              that you don't recognize for security.
            </Typography>
          </Box>

          {loading && sessions.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {sessions.map((session) => (
                <ListItem key={session.id} divider>
                  <DeviceIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">
                          {session.device_info}
                        </Typography>
                        <Chip
                          label={getSessionStatusText(session)}
                          size="small"
                          color={getSessionStatusColor(session)}
                          variant={session.is_current ? 'filled' : 'outlined'}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          IP Address: {session.ip_address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Started: {formatDistanceToNow(new Date(session.created_at))} ago
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    {!session.is_current && (
                      <IconButton
                        edge="end"
                        onClick={() => handleTerminateClick(session.id)}
                        color="error"
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <Box mt={3} display="flex" gap={2}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<WarningIcon />}
              onClick={() => setLogoutConfirmOpen(true)}
              disabled={loading}
            >
              Logout All Sessions
            </Button>
            <Button
              variant="outlined"
              onClick={loadSessions}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutConfirmOpen} onClose={() => setLogoutConfirmOpen(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Logout All Sessions</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to logout from all sessions? This will end your 
            current session and log you out from all devices.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={logoutAllSessions}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Logout All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terminate Session Confirmation */}
      <Dialog 
        open={!!sessionToTerminate} 
        onClose={() => setSessionToTerminate(null)}
      >
        <DialogTitle>Terminate Session</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to terminate this session? The user will be 
            logged out from that device.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionToTerminate(null)}>Cancel</Button>
          <Button
            onClick={handleTerminateConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Terminate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SessionManager;